import { TextInput } from "components/common";
import { Modal, ModalProps } from "components/common/Modal";
import { RadioButton } from "components/common/RadioButton";
import { useSelectValue } from "components/common/Selectbox/hook";
import { WithErrorForm } from "components/common/WithErrorForm";
import { Button } from "components/parts/Button/Button";
import { SelectBox } from "components/parts/Input/SelectBox";
import { Option } from "components/parts/Input/SelectBox/SelectBox";
import { Typography } from "components/parts/Typography/Typography";
import { ADMINISTRATOR_GROUP_ID } from "constants/constant";
import { useFormik } from "formik";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import {
  ConsoleRoleType,
  CreateUserInput,
  UpdateUserInput,
  User,
  UserLfInput,
} from "types/graphqlapi";
import {
  createUserMutation,
  deleteUserMutation,
  updateUserMutation,
} from "utils/apis/user";
import { validationSchemaUser } from "utils/validation";

import {
  StyledDiv,
  StyledForm,
  StyledFormRow,
  StyledLabel,
  StyledModalContent,
  StyledModalFooter,
  StyledModalHeader,
} from "./styled";
import { CheckBox } from "components/parts/Input/CheckBox";

type UserManageModalProps = {
  selectedUser: User;
  selectedGroup: Option | undefined;
  clearSelectedUser: () => void;
  groupListWithAdmin: Option[];
} & ModalProps;

type UserFormValueType = {
  id: string;
  name: string;
  email: string;
  role: ConsoleRoleType;
  isPersonalDataHandler: boolean;
  groupId: string;
};

export const UserManageModal: React.FC<UserManageModalProps> = ({
  closeHandler,
  selectedUser,
  selectedGroup,
  clearSelectedUser,
  groupListWithAdmin,
}) => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const { userId, selectedGroupId } = useConsoleState();
  const [userGroup, userGroupOnChangeHandler] = useSelectValue(
    selectedUser?.group_id || ADMINISTRATOR_GROUP_ID
  );
  const insertUser = async (values: UserFormValueType): Promise<void> => {
    try {
      const createUserInput: CreateUserInput = {
        group_id: userGroup,
        name: values.name,
        email: values.email,
        role: values.role,
        is_personal_data_handler: values.isPersonalDataHandler,
        groups: userGroup,
      };
      await createUserMutation(createUserInput, userId);
      clearSelectedUser();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  const updateUser = async (values: UserFormValueType): Promise<void> => {
    try {
      const updateUserInput: UpdateUserInput = {
        id: selectedUser.id,
        name: values.name,
        role: values.role,
        is_personal_data_handler: values.isPersonalDataHandler,
        group_id: userGroup,
      };
      await updateUserMutation(updateUserInput, userId);
      clearSelectedUser();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  const clickDeleteButtonHandler: React.FormEventHandler<
    HTMLButtonElement
  > = async () => {
    try {
      setLoading(true);
      const res = confirm("選択されたユーザーを削除します");
      if (res) {
        const deleteUserInput: UserLfInput = {
          id: selectedUser.id,
          email: selectedUser.email,
          group_id: selectedUser.group_id,
        };
        await deleteUserMutation(deleteUserInput, userId);
        clearSelectedUser();
      }
      setLoading(false);
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  const onSubmit = (values: UserFormValueType) => {
    setLoading(true);
    selectedUser.id ? updateUser(values) : insertUser(values);
  };

  const decideInitRole = (selectedGroupId: string) => {
    if (selectedGroupId) {
      return ConsoleRoleType.GROUPMANAGER;
    }
    return ConsoleRoleType.ADMINISTRATOR;
  };

  const formik = useFormik<UserFormValueType>({
    initialValues: {
      id: selectedUser.id || "",
      email: selectedUser.email || "",
      name: selectedUser.name || "",
      role: selectedUser.role || decideInitRole(selectedGroupId),
      isPersonalDataHandler: selectedUser.is_personal_data_handler || false,
      groupId: selectedUser.group_id || "0",
    },
    validationSchema: validationSchemaUser,
    onSubmit: onSubmit,
  });

  return (
    <Modal closeHandler={closeHandler}>
      <StyledForm onSubmit={formik.handleSubmit}>
        <StyledModalHeader>
          <Typography text="ユーザー" size="large" />
        </StyledModalHeader>
        <StyledModalContent>
          <StyledFormRow>
            <Typography text="ユーザー名" required width="10em" />
            <WithErrorForm
              touched={formik.touched.name}
              errorMessage={formik.errors.name}
            >
              <TextInput
                width="20rem"
                autoFocus
                maxlength="30"
                {...formik.getFieldProps("name")}
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography
              text="メールアドレス"
              required={!selectedUser.id}
              width="10em"
            />
            {selectedUser.id ? (
              <Typography width="20rem" text={formik.values.email} />
            ) : (
              <WithErrorForm
                touched={formik.touched.email}
                errorMessage={formik.errors.email}
              >
                <TextInput
                  width="20rem"
                  type="email"
                  maxlength="50"
                  {...formik.getFieldProps("email")}
                />
              </WithErrorForm>
            )}
          </StyledFormRow>
          {selectedGroupId == "" || selectedGroupId == undefined ? (
            <StyledFormRow>
              <Typography
                text="グループ名"
                required={!selectedUser.id}
                width="10em"
              />
              {selectedUser.id ? (
                <Typography
                  width="20rem"
                  text={
                    selectedGroup
                      ? selectedGroup.text
                      : "グループが見つかりません"
                  }
                  textAlign="left"
                />
              ) : (
                <SelectBox
                  name=""
                  value={userGroup}
                  optionList={groupListWithAdmin}
                  width="20rem"
                  onChange={(e) => {
                    userGroupOnChangeHandler(e);
                    if (e.target.value === "0") {
                      formik.values.role = ConsoleRoleType.ADMINISTRATOR;
                    } else {
                      formik.values.role = ConsoleRoleType.GROUPMANAGER;
                    }
                    formik.values.groupId = e.target.value;
                  }}
                />
              )}
            </StyledFormRow>
          ) : (
            <></>
          )}
          {userGroup == ADMINISTRATOR_GROUP_ID && (
            <StyledFormRow>
              <Typography text="権限" required width="10em" />
              <StyledDiv>
                <RadioButton
                  {...formik.getFieldProps("role")}
                  id="ADMINISTRATOR"
                  name="authority"
                  value={ConsoleRoleType.ADMINISTRATOR}
                  text="ADMINISTRATOR"
                  checked={formik.values.role === ConsoleRoleType.ADMINISTRATOR}
                  onChange={(e) => {
                    formik.setFieldValue("role", e.target.value);
                    formik.setFieldValue("isPersonalDataHandler", false);
                  }}
                />
                <RadioButton
                  {...formik.getFieldProps("role")}
                  id="ASMARQUSER"
                  name="authority"
                  value={ConsoleRoleType.ASMARQUSER}
                  text="ASMARQUSER"
                  checked={formik.values.role === ConsoleRoleType.ASMARQUSER}
                  onChange={(e) => {
                    formik.setFieldValue("role", e.target.value);
                    selectedUser.id &&
                      formik.setFieldValue(
                        "isPersonalDataHandler",
                        selectedUser.is_personal_data_handler
                      );
                  }}
                />
              </StyledDiv>
            </StyledFormRow>
          )}
          {userGroup != ADMINISTRATOR_GROUP_ID && (
            <StyledFormRow>
              <Typography text="権限" required width="10em" />
              <StyledDiv>
                <RadioButton
                  {...formik.getFieldProps("role")}
                  id="GROUPMANAGER"
                  name="authority"
                  value={ConsoleRoleType.GROUPMANAGER}
                  text="GROUPMANAGER"
                  checked={formik.values.role === ConsoleRoleType.GROUPMANAGER}
                  onChange={(e) => formik.setFieldValue("role", e.target.value)}
                />
                <RadioButton
                  {...formik.getFieldProps("role")}
                  id="GROUPUSER"
                  name="authority"
                  value={ConsoleRoleType.GROUPUSER}
                  text="GROUPUSER"
                  checked={formik.values.role === ConsoleRoleType.GROUPUSER}
                  onChange={(e) => formik.setFieldValue("role", e.target.value)}
                />
              </StyledDiv>
            </StyledFormRow>
          )}
          {formik.values.role === ConsoleRoleType.ASMARQUSER && (
            <StyledFormRow>
              <Typography text="" width="10em" />
              <StyledLabel id="checkbox-isPersonalDataHandler">
                <CheckBox
                  id="isPersonalDataHandler"
                  onChange={() =>
                    formik.setFieldValue(
                      "isPersonalDataHandler",
                      !formik.values.isPersonalDataHandler
                    )
                  }
                  checked={formik.values.isPersonalDataHandler}
                />
                <Typography text="モニターのメールアドレスを扱う" />
              </StyledLabel>
            </StyledFormRow>
          )}
        </StyledModalContent>
        <StyledModalFooter>
          {selectedUser.id && (
            <Button
              onClick={clickDeleteButtonHandler}
              variant="secondary"
              text="削除"
              type="button"
            />
          )}
          <Button text={selectedUser.id ? "更新" : "登録"} type="submit" />
        </StyledModalFooter>
      </StyledForm>
    </Modal>
  );
};
